import { Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '../../../lib/cn';
import { useAccess } from '../../../lib/useAccess';
import { modKeyLabel } from '../../../ui/Kbd';
import { toast } from '../../../ui/Toast';
import { Tooltip } from '../../../ui/Tooltip';
import { askAi, useAskAiState } from '../ask-ai-store';
import { awaitingConfirmation, useCopilotChat } from '../chat-store';
import { useCopilotStatus } from '../hooks';
import { COPILOT_DOCK_WIDTH, useIsDesktop } from './CopilotDock';

/**
 * Odstępy powiadomień (`ui/Toast`) tak, żeby nie zasłaniały bąbla ani pola wiadomości czatu: desktop — nad bąblem
 * (24 + 56 + 12 px), przy otwartym czacie na lewo od niego; telefon — nad bąblem (menu 76 + 12 + bąbel 48 + 12 px),
 * przy czacie na pełny ekran nad polem wiadomości.
 */
export function toastOffsets(desktop: boolean, chatOpen: boolean): Record<string, string> {
  if (desktop) {
    return chatOpen
      ? { '--fos-toast-right': `${COPILOT_DOCK_WIDTH + 24}px`, '--fos-toast-bottom': '24px' }
      : { '--fos-toast-right': '24px', '--fos-toast-bottom': '92px' };
  }
  const bottom = `calc(${chatOpen ? 176 : 148}px + env(safe-area-inset-bottom))`;
  return { '--fos-toast-right': '16px', '--fos-toast-bottom': bottom, '--fos-toast-bottom-mobile': bottom };
}

export const MODAL_OPEN_HINT = 'Zamknij okno, żeby zapytać Copilota';

/**
 * Inne okno modalne jest otwarte (Przegląd Fali 10): modalne warstwy Radix (Dialog, AlertDialog, menu) blokują resztę
 * strony (`pointer-events: none` na body, `aria-hidden` na sąsiadach) — czat otwarty nad nimi nie przyjmuje kliknięć,
 * a klik w niego zamyka okno i gubi jego niezapisane dane (np. szkic reguły). Czat Copilota na telefonie też jest
 * modalny, ale ten stan dotyczy tylko otwierania (zamknięty czat).
 */
export function otherModalOpen(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.body.style.pointerEvents === 'none') return true;
  return Boolean(
    document.querySelector('[role="dialog"][aria-modal="true"]:not(#copilot-panel), [role="alertdialog"]'),
  );
}

/**
 * Bąbel AI (Fala 10, DECISIONS 2026-10-07 [S] „Copilot ma być prawdziwym asystentem” pkt 1): okrągły przycisk
 * w prawym dolnym rogu każdego ekranu po zalogowaniu (montuje go AppShell), ikona sparkles na akcencie. Otwiera panel
 * czatu Copilota („Zapytaj AI”) z kontekstem bieżącego ekranu. Skrót ⌘J / Ctrl+J otwiera i zamyka, Esc zamyka panel.
 *
 * - Telefon: nad dolnym menu (12 px nad nim, nie zasłania go), 48 px; środkowy mikrofon dolnego menu otwiera ten sam
 *   czat i od razu dyktuje (10.10.2026, DECISIONS [S] „Mikrofon w dolnym menu = Copilot”; bez AI — skróty ręczne).
 *   Na ekranach z akcjami przypiętymi nad dolnym menu (profil klienta) bąbel jest nad nimi (klasa
 *   `fos-mobile-actionbar` na pasku akcji ekranu).
 * - Plakietka: Copilot czeka na Twoje potwierdzenie (podgląd zadania, wpisu czasu, zlecenia albo wybór klienta).
 * - AI wyłączone: spokojny, szary bąbel z podpowiedzią „AI wyłączone — włącz w Ustawieniach” (panel mówi to samo).
 * - AI wyłączone dla osoby (limit AI 0 zł, np. partner w cudzym workspace'ie): ten sam szary bąbel z komunikatem
 *   `copilot.status` („Jako partner korzystasz z AI we własnym workspace'ie…”), zanim osoba cokolwiek wyśle.
 */
export function CopilotBubble({ className }: { className?: string }) {
  const state = useAskAiState();
  const status = useCopilotStatus();
  const access = useAccess();
  const { turns } = useCopilotChat();
  const waiting = !state.open && awaitingConfirmation(turns);
  const memberBlocked = Boolean(status.data?.memberBlock);
  const disabled = status.data ? !status.data.enabled || memberBlocked : false;
  const desktop = useIsDesktop();

  useEffect(() => {
    const root = document.documentElement.style;
    const vars = toastOffsets(desktop, state.open);
    for (const [name, value] of Object.entries(vars)) root.setProperty(name, value);
    return () => {
      for (const name of Object.keys(vars)) root.removeProperty(name);
    };
  }, [desktop, state.open]);

  // ⌘J / Ctrl+J: otwórz albo zamknij czat z każdego miejsca aplikacji (także z pola tekstowego); przy otwartym innym
  // oknie modalnym czat się nie otwiera (patrz `otherModalOpen`) — zamknąć go skrótem można zawsze
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        if (!askAi.getSnapshot().open && otherModalOpen()) {
          toast.info(MODAL_OPEN_HINT, { id: 'copilot-modal-open' });
          return;
        }
        askAi.toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const shortcut = `${modKeyLabel()}+J`;
  const hint = disabled
    ? memberBlocked
      ? (status.data?.message ?? 'AI wyłączone w tym workspace')
      : access.isManagerOrAbove
        ? 'AI wyłączone — włącz w Ustawieniach'
        : 'AI wyłączone — włącza je właściciel albo manager w Ustawieniach'
    : waiting
      ? 'Copilot czeka na Twoje potwierdzenie'
      : `Zapytaj Copilota (${shortcut})`;
  const label = disabled ? `Copilot: ${hint}` : waiting ? `Otwórz Copilota — ${hint.toLowerCase()}` : 'Otwórz Copilota';

  return (
    <Tooltip content={hint} side="left">
      <button
        type="button"
        onClick={() => askAi.toggle()}
        aria-label={label}
        aria-expanded={state.open}
        aria-controls={state.open ? 'copilot-panel' : undefined}
        aria-keyshortcuts="Control+J Meta+J"
        data-copilot-bubble=""
        data-state={disabled ? 'disabled' : waiting ? 'waiting' : 'ready'}
        className={cn(
          'fos-copilot-bubble fixed z-40 inline-flex items-center justify-center rounded-full transition-[transform,box-shadow,background-color]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent active:scale-95',
          // telefon: nad dolnym menu (76 px + bezpieczny obszar) i nad paskiem akcji ekranu, 48 px
          'right-4 bottom-[calc(76px+env(safe-area-inset-bottom)+12px)] size-12',
          'max-md:[body:has(.fos-mobile-actionbar)_&]:bottom-[calc(76px+66px+env(safe-area-inset-bottom)+12px)]',
          // desktop: prawy dolny róg, 56 px
          'md:right-6 md:bottom-6 md:size-14',
          disabled
            ? 'border border-line bg-panel text-fg-muted shadow-popover hover:text-fg'
            : 'bg-accent text-on-accent shadow-modal hover:bg-accent-hover',
          className,
        )}
      >
        <Sparkles size={22} strokeWidth={1.75} aria-hidden />
        {waiting && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 inline-flex size-3.5 rounded-full border-2 border-canvas bg-warning"
          />
        )}
      </button>
    </Tooltip>
  );
}
